/**
 * TSS (Training Stress Score) estimation for activities where Strava's
 * Relative Effort (suffer_score) is unavailable — e.g. free Strava accounts.
 *
 * Priority order:
 *  Cycling: power TSS (NP ÷ FTP)²  →  hrTSS fallback
 *  Running / Swimming / other: hrTSS (avgHR ÷ LTHR)²
 *
 * Standard formulas:
 *  hrTSS    = (duration_hrs) × IF² × 100   where IF = avgHR ÷ LTHR
 *  powerTSS = (duration_hrs) × IF² × 100   where IF = NP ÷ FTP
 *
 * References: Coggan & McGregor; Friel "The Triathlete's Training Bible"
 */

const LTHR_FRACTION_OF_MAX = 0.88; // Friel's standard LTHR ≈ 88 % of max HR
const MAX_TSS_PER_WORKOUT  = 500;  // safety cap — prevents runaway values

/**
 * Resolve Lactate Threshold Heart Rate from profile metrics.
 * Returns null if there is insufficient data to make a reliable estimate.
 */
function resolveLTHR(metrics) {
  const lthr = Number(metrics.lthr);
  if (lthr > 0) return lthr;
  const maxHR = Number(metrics.maxHR);
  if (maxHR > 0) return Math.round(maxHR * LTHR_FRACTION_OF_MAX);
  return null;
}

/**
 * Estimate TSS for a single workout from available sensor data.
 * Returns { value: number, estimated: true } or null if not estimable.
 *
 * @param {object} workout  - Normalised workout object (from stravaAPI / intervalsAPI)
 * @param {object} metrics  - Athlete profile metrics (ftp, lthr, maxHR, …)
 */
export function estimateTSS(workout, metrics = {}) {
  const durationHrs = (workout.duration || 0) / 3600;
  if (durationHrs < 1 / 60) return null; // ignore activities shorter than 1 minute

  const isCycling = workout.type === 'bike';

  // ── Power-based TSS (cycling only) ───────────────────────────────────────
  if (isCycling) {
    const ftp = Number(metrics.ftp);
    const np  = workout.weightedAveragePower || workout.averagePower || 0;
    if (ftp > 0 && np > 0) {
      const IF  = np / ftp;
      const tss = durationHrs * IF * IF * 100;
      return { value: Math.round(Math.min(tss, MAX_TSS_PER_WORKOUT)), estimated: true };
    }
  }

  // ── HR-based TSS (all sports) ─────────────────────────────────────────────
  const lthr  = resolveLTHR(metrics);
  const avgHR = workout.averageHeartRate || 0;
  if (lthr && avgHR > 0) {
    const IF  = avgHR / lthr;
    const tss = durationHrs * IF * IF * 100;
    return { value: Math.round(Math.min(tss, MAX_TSS_PER_WORKOUT)), estimated: true };
  }

  return null; // not enough data
}

/**
 * Enrich an array of workouts: fill estimated TSS only where tss is absent.
 * Workouts that already have a real TSS value are returned unchanged.
 * Enriched workouts gain a `tssEstimated: true` flag.
 *
 * @param {object[]} workouts - Raw workout array
 * @param {object}   metrics  - Athlete profile metrics
 * @returns {object[]} New array (original objects untouched)
 */
export function enrichWorkoutsWithTSS(workouts, metrics = {}) {
  return workouts.map(w => {
    if (w.tss != null && w.tss > 0) return w; // real value present — leave untouched
    const est = estimateTSS(w, metrics);
    if (!est) return w;
    return { ...w, tss: est.value, tssEstimated: true };
  });
}
